import { type JSX, useEffect, useRef, useState } from "react"
import { Modal } from "../shared/modal"

const CONFIRM_WORD = "DELETE"

// Deleting a workspace stops every agent in it, so this asks for the word
// rather than for a click: a confirm button next to a ⋯ menu is one slip away
// from the thing you meant to open.
//
// Submits a real form rather than fetching, so the server answers with its
// redirect and flash exactly as it does for every other destructive action on
// the site. That is also why the CSRF token and _method ride in hidden inputs:
// this is the same request button_to used to send.
export function DeleteDialog({
  open,
  workspaceName,
  deleteUrl,
  onClose,
}: {
  open: boolean
  workspaceName: string
  deleteUrl: string
  onClose: () => void
}): JSX.Element {
  const [typed, setTyped] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  // A dialog reopened after a cancel should not still be armed.
  useEffect(() => {
    if (open) {
      setTyped("")
      // showModal moves focus to the dialog; put it in the field.
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  const armed = typed.trim() === CONFIRM_WORD

  return (
    <Modal open={open} title="Delete workspace" onClose={onClose}>
      <form method="post" action={deleteUrl}>
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value={csrfToken()} />

        <p className="modal__text">
          Are you sure you want to delete the <strong>{workspaceName}</strong> workspace?
          Its agents will stop working and its sandbox will be shut down.
        </p>

        <label className="share__confirm-label" htmlFor="delete-confirm">
          Type <strong>{CONFIRM_WORD}</strong> to confirm
        </label>
        <input
          id="delete-confirm"
          ref={inputRef}
          className="share__confirm-input"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoComplete="off"
          // Browsers offer to remember anything named; this is not worth keeping.
          name="confirmation"
          placeholder={CONFIRM_WORD}
        />

        <div className="modal__actions">
          <button type="button" className="btn btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--sm btn--danger" disabled={!armed}>
            Delete workspace
          </button>
        </div>
      </form>
    </Modal>
  )
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}
